import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';
import { UserApplications, UserDto } from '@workspace/data-types';
import { environment } from '@workspace/ng-config';
import { ApplicationStore, UserStore } from '@workspace/ng-data-access';

export interface SwitchableApp {
  id: UserApplications;
  label: string;
  shortLabel: string;
  description: string;
  icon: string;
  url: string;
  tooltip: string;
}

@Component({
  selector: 'aura-profile-app-switcher',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButton, MatIcon, MatMenu, MatMenuItem, MatMenuTrigger, MatTooltip],
  templateUrl: './profile-app-switcher.component.html',
})
export class ProfileAppSwitcherComponent {
  private readonly userStore = inject(UserStore);
  private readonly appStore = inject(ApplicationStore);
  private readonly appLinks = environment.apps;

  readonly user = input<UserDto | null>(null);

  protected readonly currentUser = computed(() => this.user() ?? this.userStore.currentUser());

  protected readonly currentApp = computed(() => {
    const storeName = this.appStore.currentApplicationName();
    if (storeName) return storeName;

    if (typeof window !== 'undefined') {
      const pathname = window.location.pathname;
      const hostname = window.location.hostname;
      if (hostname.startsWith('admin.')) return UserApplications.ADMIN;
      if (pathname.startsWith('/owner')) return UserApplications.OWNER;
      if (pathname.startsWith('/agent')) return UserApplications.AGENT;
      return UserApplications.PUBLIC;
    }
    return UserApplications.PUBLIC;
  });

  protected readonly isAuthenticated = computed(() => !!this.currentUser());
  protected readonly hasOwnerProfile = computed(() => !!this.currentUser()?.profiles?.owner);
  protected readonly hasAgentProfile = computed(() => !!this.currentUser()?.profiles?.agent);
  protected readonly hasAnyProfile = computed(() => this.hasOwnerProfile() || this.hasAgentProfile());

  protected readonly accessibleApps = computed<SwitchableApp[]>(() => {
    if (!this.isAuthenticated()) {
      return [];
    }

    const hasAny = this.hasAnyProfile();
    const showOwner = this.hasOwnerProfile() || !hasAny;
    const showAgent = this.hasAgentProfile() || !hasAny;

    const apps: SwitchableApp[] = [];

    if (showOwner && this.appLinks?.ownerUrl) {
      apps.push({
        id: UserApplications.OWNER,
        label: 'Espace propriétaire',
        shortLabel: 'Propriétaire',
        description: this.hasOwnerProfile() ? 'Gérer mes biens et mandats' : 'Vendre un bien immobilier',
        icon: 'house',
        url: this.appLinks.ownerUrl,
        tooltip: "Accéder à l'espace propriétaire",
      });
    }

    if (showAgent && this.appLinks?.agentUrl) {
      apps.push({
        id: UserApplications.AGENT,
        label: 'Espace agent pro',
        shortLabel: 'Agent',
        description: this.hasAgentProfile() ? 'Mandats et candidatures' : 'Devenir agent certifié',
        icon: 'briefcase',
        url: this.appLinks.agentUrl,
        tooltip: "Accéder à l'espace agent",
      });
    }

    if (this.appLinks?.publicUrl) {
      apps.push({
        id: UserApplications.PUBLIC,
        label: 'Site public',
        shortLabel: 'Public',
        description: 'Recherche et vitrine',
        icon: 'globe',
        url: this.appLinks.publicUrl,
        tooltip: 'Revenir au site public',
      });
    }

    return apps;
  });

  protected readonly destinations = computed<SwitchableApp[]>(() => {
    const current = this.currentApp();
    return this.accessibleApps().filter((app) => app.id !== current);
  });

  protected readonly isVisible = computed(() => {
    if (this.currentApp() === UserApplications.ADMIN) {
      return false;
    }
    return true;
  });

  protected readonly singleDestination = computed<SwitchableApp | null>(() => {
    const dests = this.destinations();
    return dests.length === 1 ? dests[0] : null;
  });

  protected readonly hasMultipleDestinations = computed(() => this.destinations().length > 1);

  protected readonly menuButtonLabel = computed(() => {
    return this.hasAnyProfile() ? "Changer d'espace" : 'Accéder aux espaces';
  });

  protected readonly menuTitle = computed(() => {
    return this.hasAnyProfile() ? "Changer d'espace" : 'Choisir un espace';
  });
}
